<template>
  <StagedPreselectBlock
    :title="title"
    :fields="fields"
    :color-schema="colorSchema"
    :show-full-width="showFullWidth"
    :bg-color="bgColor"
    :text-color="textColor"
    :settings="settings"
    :staged="staged"
  >
    <div
      v-if="redirectUrl"
      class="preselect__row preselect__row--centered preselect__show-selection"
    >
      <label class="preselect__label form__label">{{ personalSelectionLabel }}</label>
      <div class="preselect__personal-selection">
        <span class="button__container">
          <button
            type="button"
            class="button button--secondary button--small button--bigtext button--collapsed"
            @click="goToResults"
          >
            <span class="button__label">{{ showSelectionLabel }}</span>
          </button>
        </span>
      </div>
    </div>
  </StagedPreselectBlock>
</template>

<script setup lang="ts">
import axios from 'axios';
import { onMounted, ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';
import { useTranslation } from '@shop/utils/Translation';

import StagedPreselectBlock from './StagedPreselectBlock.vue';
import { useStagedPreselect } from './useStagedPreselect';

import type { CalfCircumferencePreselectBlockProps } from './CalfCircumferencePreselectBlock.types';
import type { PreselectOption } from './StagedPreselectBlock.types';

const props = defineProps<CalfCircumferencePreselectBlockProps>();

const { t } = useTranslation('calfsize_tool');

const redirectUrl = ref<string | null>(null);

const attributes = props.fields.map(field => field.name);

const initialOptions = (() => {
  const opts: Record<string, PreselectOption[]> = {};
  props.fields.forEach(field => {
    if (field.options?.length) {
      opts[field.name] = field.options;
    }
  });
  return opts;
})();

const getRedirectUrl = async (): Promise<void> => {
  const shoeSize = staged.selectedOptions.shoe_size;
  const calfCircumference = staged.selectedOptions.calf_circumference;

  if (!shoeSize || !calfCircumference) {
    redirectUrl.value = null;
    return;
  }

  staged.loading.value = true;

  try {
    const response = await axios.get<{ redirect?: string }>(
      generateUrl('category_tool_calfsize', {
        shoe_size: shoeSize,
        calf_circumference: calfCircumference,
      })
    );
    redirectUrl.value = response.data.redirect ?? null;
  } catch {
    redirectUrl.value = null;
  } finally {
    staged.loading.value = false;
  }
};

const staged = useStagedPreselect({
  attributes,
  optionsRoute: '',
  initialOptions,
  skipGetOptions: true,
  onChange: () => {
    getRedirectUrl().catch(() => undefined);
  },
});

const goToResults = (): void => {
  if (redirectUrl.value) {
    window.location.href = redirectUrl.value;
  }
};

const personalSelectionLabel = props.personalSelectionLabel ?? t.value('filter.personal_selection');
const showSelectionLabel = props.showSelectionLabel ?? t.value('filter.show_selection');

onMounted(() => {
  getRedirectUrl().catch(() => undefined);
});
</script>
